import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router";
import { useMutation, useQuery } from "convex/react";
import { AnimatePresence, Reorder, motion, useReducedMotion } from "motion/react";
import { CalendarClock, Globe, GripVertical, Layers, Plus, Power, ShieldAlert, Trash2 } from "lucide-react";

import { TransientStatusBanner } from "../../components/feedback/TransientStatusBanner";
import { useTransientMessage } from "../../components/feedback/useTransientMessage";
import { TAB_BODY_COMPACT_TIMEFRAME_CLASS, TabPageHeader } from "../../components/navigation/TabPageHeader";
import { TabPageShell } from "../../components/navigation/TabPageShell";
import { mutationRef, queryRef } from "../../lib/convex/refs";
import { useDeviceId } from "../../lib/device/DeviceProvider";
import { mergeMarketingDemoRules, mergeMarketingDemoTargets } from "../../lib/marketing/demoRules";
import { useMarketingDemoMode } from "../../lib/marketing/MarketingDemoProvider";
import { triggerReorderEnterHaptic } from "../../lib/native/haptics";
import { canPickAndroidApps, openAndroidOverlaySettings } from "../../lib/native/rules";
import { useNativeAppBlockingStatus } from "../../lib/native/useNativeAppBlockingStatus";
import { openUsagePermissionSettings } from "../../lib/native/usage";
import { deriveActiveBlockedDomains } from "../../lib/native/webFilter";
import {
  androidAppBlockingGateReason,
  canClaimNativeAppEnforcement,
} from "../../lib/rules/appBlockingHealth";
import { syncEnforcementToNative, getDeviceTimeZone, projectRulesAfterResume } from "../../lib/rules/enforcementSync";
import {
  emptyRuleDraft,
  emptyTargetDraft,
  formatDays,
  formatWindow,
  ruleConfigFromDraft,
  sanitizeTargetDraft,
  targetConfigFromDraft,
  targetContentSummary,
} from "../../lib/rules/ruleDerivation";
import { applyStoredOrder } from "../../lib/rules/listOrder";
import type { Rule, RuleConfig, RuleDraft, Target, TargetConfig, TargetDraft } from "../../lib/rules/ruleTypes";
import { getEnforcingRules, getRulesOnBreakPause, isUserForcedStopSuppression } from "../../lib/rules/ruleSchedule";
import { getDeviceLocalScheduleParts } from "../../lib/rules/ruleScheduleTimeZone";
import { useEnforcementClock } from "../../lib/rules/useEnforcementClock";
import {
  addOptimisticEnforcementRule,
  clearEnforcementProjection,
  clearOptimisticEnforcementRule,
  getEnforcementProjection,
  isEnforcementProjectionSynced,
  mergeRulesWithOptimistic,
  setEnforcementProjection,
  useOptimisticEnforcementRules,
} from "./enforcementOptimistic";
import { ActiveEnforcementBanner } from "./ActiveEnforcementBanner";
import { VpnInstallPromptModal } from "./VpnInstallPromptModal";
import { RuleEditorSheet } from "./RuleEditorSheet";
import { SmartBlockRuleWindowDialog } from "./SmartBlockRuleWindowDialog";
import { TargetEditorSheet, type TargetEditorOrigin } from "./TargetEditorSheet";
import {
  isRuleActivelyEnforcingForSwipeGuard,
  isRuleFullyCoveredByAnotherEnforcingRule,
  isTargetFullyCoveredByAnotherEnforcingRule,
  isTargetLockedByEnforcingRules,
  nudgeSwipeUnlock,
  requiresSwipeUnlockToMutateRule,
} from "./swipeUnlockGuards";
import { QuickStartFab } from "./QuickStartFab";
import { QuickStartDrawer } from "./QuickStartDrawer";
import {
  DEFAULT_QUICK_DURATION_MINUTES,
  QUICK_SESSION_BACKUP_CHECK_MS,
  QUICK_SESSION_SOURCE,
  buildAutoSessionName,
  filterVisibleRules,
  findActiveQuickSession,
  isQuickSessionScheduleActive,
  msUntilSessionEnd,
  pruneToAvailableTargetIds,
  sanitizeQuickDuration,
  type QuickSessionEntry,
  type SavedQuickSession,
} from "./quickStartSession";
import {
  CardAppIcons,
  CardWebsites,
  CardWebsiteCategories,
  SummaryWithViewRow,
  dedupeDomainsFromTargets,
  ruleBlockSummaryText,
} from "./TargetPreview";
import {
  iconAppsForRule,
  iconAppsForTarget,
  ruleDisplaysSmartBlockBadge,
  targetDisplaysSmartBlockBadge,
} from "../../lib/insights/smartBlockSelection";
import { useScreen0Language, getScreen0IntlLocale } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";

const POPOVER_SPRING = { type: "spring" as const, stiffness: 520, damping: 36, mass: 0.7 };
const BANNER_SPRING = { type: "spring" as const, stiffness: 520, damping: 36, mass: 0.7 };
const CARD_BODY_ACTIVE_CLASS = "transition-colors active:bg-[oklch(20%_0.02_165)]";

const HIGHLIGHT_CSS = `
@keyframes rule-row-pulse {
  0%, 100% { background-color: oklch(22% 0.04 155 / 0); }
  50% { background-color: oklch(28% 0.05 155 / 0.55); }
}
.rule-row-highlight {
  animation: rule-row-pulse 0.9s ease-in-out 2;
  border-radius: 8px;
}
@keyframes rule-row-insight-focus {
  0% {
    background-color: oklch(22% 0.04 155 / 0);
    box-shadow: inset 0 0 0 0 oklch(72% 0.09 155 / 0);
  }
  35% {
    background-color: oklch(24% 0.05 155 / 0.35);
    box-shadow: inset 0 0 0 2px oklch(72% 0.09 155 / 0.65);
  }
  100% {
    background-color: oklch(22% 0.04 155 / 0);
    box-shadow: inset 0 0 0 0 oklch(72% 0.09 155 / 0);
  }
}
.rule-row-insight-highlight {
  animation: rule-row-insight-focus 1.1s cubic-bezier(0.16, 1, 0.3, 1) 1;
  border-radius: 8px;
}
@media (prefers-reduced-motion: reduce) {
  .rule-row-highlight {
    animation: none;
    background-color: oklch(28% 0.05 155 / 0.55);
  }
  .rule-row-insight-highlight {
    animation: none;
    background-color: oklch(24% 0.05 155 / 0.35);
    box-shadow: inset 0 0 0 2px oklch(72% 0.09 155 / 0.65);
  }
}
`;

type RulesNavigationState = {
  highlightRuleId?: string;
  bannerMessage?: string;
  highlightSource?: "insight";
};

type RuleHighlightVariant = "activation" | "insight";

const WOBBLE_CSS = `
@keyframes card-wobble {
  0%   { transform: rotate(-0.9deg); }
  50%  { transform: rotate(0.9deg); }
  100% { transform: rotate(-0.9deg); }
}
.wobble-card {
  animation: card-wobble 0.55s ease-in-out infinite;
  transform-origin: center center;
}
`;

type RulePrefs = { ruleOrder?: string[]; targetOrder?: string[] };
type RulePrefsPatch = { ruleOrder?: string[]; targetOrder?: string[] };

type DeleteConfirmState = { kind: "rule" | "target"; id: string; covered?: boolean } | null;
type SmartBlockNotEditableState = { kind: "rule" | "target" } | null;

const rulePrefsRef = queryRef<{ anonymousDeviceId: string }, RulePrefs | null>("rulePrefs:getPrefs");
const upsertRulePrefsRef = mutationRef<{ anonymousDeviceId: string; patch: RulePrefsPatch }, null>(
  "rulePrefs:upsertPrefs",
);

type QuickStartPrefs = {
  fabIcon: string;
  recentSessions: QuickSessionEntry[];
  savedSessions: SavedQuickSession[];
};

const quickStartPrefsRef = queryRef<{ anonymousDeviceId: string }, QuickStartPrefs>("quickStartPrefs:get");

export function RulesTab() {
  const prefersReducedMotion = useReducedMotion();
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const anonymousDeviceId = useDeviceId();
  const location = useLocation();
  const navigate = useNavigate();
  const isNativePlatform = Capacitor.isNativePlatform();
  const isAndroid = isNativePlatform && Capacitor.getPlatform() === "android";
  const { appBlocking, setAppBlocking, refresh: refreshAppBlocking } = useNativeAppBlockingStatus();
  const rulesReal = useQuery(queryRef<{ anonymousDeviceId: string }, Rule[]>("rules:list"), { anonymousDeviceId });
  const targetsReal = useQuery(queryRef<{ anonymousDeviceId: string }, Target[]>("targets:list"), { anonymousDeviceId });
  const { isActive: marketingDemoActive, seedApps: marketingSeedApps } = useMarketingDemoMode();
  const rules = useMemo(
    () => mergeMarketingDemoRules(rulesReal, marketingDemoActive, marketingSeedApps),
    [marketingDemoActive, marketingSeedApps, rulesReal],
  );
  const targets = useMemo(
    () => mergeMarketingDemoTargets(targetsReal, marketingDemoActive, marketingSeedApps),
    [marketingDemoActive, marketingSeedApps, targetsReal],
  );
  const prefs = useQuery(rulePrefsRef, { anonymousDeviceId });
  const quickStartPrefs = useQuery(quickStartPrefsRef, { anonymousDeviceId });

  const upsertRule = useMutation(mutationRef<{ anonymousDeviceId: string; id?: string; rule: RuleConfig }, { id: string }>("rules:upsert"));
  const updateSmartBlockWindow = useMutation(
    mutationRef<
      { anonymousDeviceId: string; id: string; window: RuleConfig["window"] },
      { id: string }
    >("rules:updateSmartBlockWindow"),
  );
  const setRuleEnabled = useMutation(mutationRef<{ anonymousDeviceId: string; id: string; enabled: boolean }, { id: string }>("rules:setEnabled"));
  const resumeForcedStopMutation = useMutation(
    mutationRef<
      { anonymousDeviceId: string; id: string; nowMs: number; timeZone: string },
      { id: string }
    >("rules:resumeForcedStop"),
  );
  const removeRuleMutation = useMutation(mutationRef<{ anonymousDeviceId: string; id: string }, { id: string }>("rules:remove"));
  const startQuickSessionMutation = useMutation(
    mutationRef<
      {
        anonymousDeviceId: string;
        targetIds: string[];
        startMinute: number;
        dayOfWeek: number;
        durationMinutes: number;
      },
      { id: string; window: RuleConfig["window"] }
    >("rules:startQuickSession"),
  );
  const recordRecentSession = useMutation(
    mutationRef<
      { anonymousDeviceId: string; targetIds: string[]; durationMinutes: number; autoName: string },
      null
    >("quickStartPrefs:recordRecent"),
  );
  const saveQuickSession = useMutation(
    mutationRef<
      { anonymousDeviceId: string; targetIds: string[]; durationMinutes: number; name: string },
      null
    >("quickStartPrefs:saveFromRecent"),
  );
  const setQuickStartFabIcon = useMutation(
    mutationRef<{ anonymousDeviceId: string; fabIcon: string }, null>("quickStartPrefs:setFabIcon"),
  );
  const upsertTarget = useMutation(mutationRef<{ anonymousDeviceId: string; id?: string; target: TargetConfig }, { id: string }>("targets:upsert"));
  const removeTargetMutation = useMutation(mutationRef<{ anonymousDeviceId: string; id: string }, { id: string }>("targets:remove"));
  const upsertRulePrefs = useMutation(upsertRulePrefsRef);

  const [ruleDraft, setRuleDraft] = useState<RuleDraft | null>(null);
  const [targetDraft, setTargetDraft] = useState<TargetDraft | null>(null);
  const [targetBaseline, setTargetBaseline] = useState<TargetDraft>(emptyTargetDraft());
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [saving, setSaving] = useState<"rule" | "target" | null>(null);
  const { message, variant: messageVariant, show: showMessage, dismiss: dismissMessage } = useTransientMessage();
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>(null);
  const [smartBlockNotEditable, setSmartBlockNotEditable] = useState<SmartBlockNotEditableState>(null);
  const [smartBlockRuleEditor, setSmartBlockRuleEditor] = useState<Rule | null>(null);
  const [ruleOrder, setRuleOrder] = useState<string[]>([]);
  const [targetOrder, setTargetOrder] = useState<string[]>([]);
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderRules, setReorderRules] = useState<Rule[]>([]);
  const [reorderTargets, setReorderTargets] = useState<Target[]>([]);
  const [highlightRuleId, setHighlightRuleId] = useState<string | null>(null);
  const [highlightVariant, setHighlightVariant] = useState<RuleHighlightVariant | null>(null);
  const optimisticEnforcementRules = useOptimisticEnforcementRules();
  const currentRules = useMemo(() => mergeRulesWithOptimistic(rules ?? []), [rules, optimisticEnforcementRules]);
  const { now: scheduleNow, refreshNow: refreshScheduleNow } = useEnforcementClock(currentRules);
  const [vpnInstallPromptOpen, setVpnInstallPromptOpen] = useState(false);
  const [quickStartOpen, setQuickStartOpen] = useState(false);
  const [quickStartTargetIds, setQuickStartTargetIds] = useState<string[]>([]);
  const [quickStartDuration, setQuickStartDuration] = useState(DEFAULT_QUICK_DURATION_MINUTES);
  const [quickStartTargetEditor, setQuickStartTargetEditor] = useState(false);
  const [targetEditorOrigin, setTargetEditorOrigin] = useState<TargetEditorOrigin>("standalone");
  const vpnInstallPromptDismissedKey = useRef<string | null>(null);
  const prefsHydrated = useRef(false);

  const lastRuleDraft = useRef<RuleDraft>(emptyRuleDraft());
  const lastTargetDraft = useRef<TargetDraft>(emptyTargetDraft());

  const openTargetEditor = useCallback((draft: TargetDraft, origin: TargetEditorOrigin) => {
    const snapshot = sanitizeTargetDraft(draft);
    setTargetBaseline(snapshot);
    setTargetEditorOrigin(origin);
    setTargetDraft(snapshot);
  }, []);
  if (ruleDraft) lastRuleDraft.current = ruleDraft;
  if (targetDraft) lastTargetDraft.current = targetDraft;

  useEffect(() => {
    if (prefs === undefined || prefsHydrated.current) return;
    prefsHydrated.current = true;
    if (prefs?.ruleOrder?.length) setRuleOrder(prefs.ruleOrder);
    if (prefs?.targetOrder?.length) setTargetOrder(prefs.targetOrder);
  }, [prefs]);

  useEffect(() => {
    const state = location.state as RulesNavigationState | null;
    if (!state?.highlightRuleId && !state?.bannerMessage) return;
    if (state.bannerMessage) showMessage(state.bannerMessage, "info");
    if (state.highlightRuleId) {
      setReorderMode(false);
      setHighlightRuleId(state.highlightRuleId);
      setHighlightVariant(state.highlightSource === "insight" ? "insight" : "activation");
    }
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, showMessage]);

  useEffect(() => {
    if (!highlightRuleId || rules === undefined) return;
    const row = document.getElementById(`rule-row-${highlightRuleId}`);
    if (!row) return;
    row.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "center" });
    const duration = prefersReducedMotion ? 0 : highlightVariant === "insight" ? 1200 : 1800;
    const timer = window.setTimeout(() => {
      setHighlightRuleId(null);
      setHighlightVariant(null);
    }, duration);
    return () => window.clearTimeout(timer);
  }, [highlightRuleId, highlightVariant, rules, prefersReducedMotion]);

  const persistOrder = useCallback(
    (patch: RulePrefsPatch) => {
      void upsertRulePrefs({ anonymousDeviceId, patch });
    },
    [anonymousDeviceId, upsertRulePrefs],
  );

  const visibleRules = useMemo(() => filterVisibleRules(currentRules), [currentRules]);
  const currentTargets = useMemo(() => targets ?? [], [targets]);
  const targetsById = useMemo(() => new Map(currentTargets.map((target) => [target._id, target])), [currentTargets]);
  const displayedRules = useMemo(
    () => applyStoredOrder(visibleRules, ruleOrder.length > 0 ? ruleOrder : prefs?.ruleOrder),
    [visibleRules, ruleOrder, prefs?.ruleOrder],
  );
  const displayedTargets = useMemo(
    () => applyStoredOrder(currentTargets, targetOrder.length > 0 ? targetOrder : prefs?.targetOrder),
    [currentTargets, targetOrder, prefs?.targetOrder],
  );
  const activeQuickSession = useMemo(() => findActiveQuickSession(currentRules), [currentRules]);

  useEffect(() => {
    if (!activeQuickSession) return;

    const removeIfExpired = () => {
      if (!isQuickSessionScheduleActive(activeQuickSession, new Date())) {
        void removeRuleMutation({ anonymousDeviceId, id: activeQuickSession._id });
      }
    };

    const timeoutMs = msUntilSessionEnd(activeQuickSession.window) + 1_000;
    const timeoutId = window.setTimeout(removeIfExpired, timeoutMs);
    const intervalId = window.setInterval(removeIfExpired, QUICK_SESSION_BACKUP_CHECK_MS);
    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, [activeQuickSession, anonymousDeviceId, removeRuleMutation]);

  useEffect(() => {
    if (targets === undefined) return;
    setQuickStartTargetIds((ids) => {
      const pruned = pruneToAvailableTargetIds(ids, targetsById);
      return pruned.length === ids.length ? ids : pruned;
    });
  }, [targets, targetsById]);

  const applyQuickSession = useCallback(
    async (targetIds: string[], durationMinutes: number) => {
      const validTargetIds = pruneToAvailableTargetIds(targetIds, targetsById);
      if (validTargetIds.length === 0) {
        showMessage(t.rules.quickStart.noValidTargets, "error");
        return;
      }
      const now = new Date();
      const safeDuration = sanitizeQuickDuration(durationMinutes);
      try {
        const { id, window } = await startQuickSessionMutation({
          anonymousDeviceId,
          targetIds: validTargetIds,
          startMinute: now.getHours() * 60 + now.getMinutes(),
          dayOfWeek: now.getDay(),
          durationMinutes: safeDuration,
        });
        const createdAt = Date.now();
        addOptimisticEnforcementRule({
          _id: id,
          anonymousDeviceId,
          createdAt,
          updatedAt: createdAt,
          enabled: true,
          targetIds: validTargetIds,
          days: [now.getDay()],
          window,
          source: QUICK_SESSION_SOURCE,
        });
        refreshScheduleNow();
        setQuickStartOpen(false);
        const autoName = buildAutoSessionName(
          validTargetIds,
          targetsById,
          t.rules.quickStart.defaultSessionName,
        );
        await recordRecentSession({
          anonymousDeviceId,
          targetIds: validTargetIds,
          durationMinutes: safeDuration,
          autoName,
        });
      } catch (error) {
        showMessage(error instanceof Error ? error.message : t.rules.ruleSaveFailed, "error");
      }
    },
    [
      anonymousDeviceId,
      recordRecentSession,
      refreshScheduleNow,
      showMessage,
      startQuickSessionMutation,
      t.rules.quickStart.defaultSessionName,
      t.rules.quickStart.noValidTargets,
      t.rules.ruleSaveFailed,
      targetsById,
    ],
  );

  const toggleReorder = () => {
    if (!reorderMode) {
      triggerReorderEnterHaptic();
      setReorderRules(displayedRules);
      setReorderTargets(displayedTargets);
      setReorderMode(true);
      return;
    }
    const nextRuleOrder = reorderRules.map((rule) => rule._id);
    const nextTargetOrder = reorderTargets.map((target) => target._id);
    setRuleOrder(nextRuleOrder);
    setTargetOrder(nextTargetOrder);
    persistOrder({ ruleOrder: nextRuleOrder, targetOrder: nextTargetOrder });
    setReorderMode(false);
  };

  useEffect(() => {
    const domains = deriveActiveBlockedDomains(currentRules, currentTargets, scheduleNow);
    const domainsKey = domains.join(",");
    void syncEnforcementToNative(currentRules, currentTargets, scheduleNow).then((result) => {
      setAppBlocking(result.appBlocking);
      if (result.needsVpnInstall) {
        if (vpnInstallPromptDismissedKey.current !== domainsKey) {
          setVpnInstallPromptOpen(true);
        }
        return;
      }
      setVpnInstallPromptOpen(false);
      vpnInstallPromptDismissedKey.current = null;
    });
  }, [currentRules, currentTargets, scheduleNow, setAppBlocking]);

  // After returning from Usage Access / overlay Settings, re-apply so the FGS can start.
  useEffect(() => {
    if (!isAndroid) return;
    const listener = App.addListener("appStateChange", (event) => {
      if (!event.isActive) return;
      void syncEnforcementToNative(currentRules, currentTargets, new Date()).then((result) => {
        setAppBlocking(result.appBlocking);
      });
    });
    return () => {
      void listener.then((handle) => handle.remove());
    };
  }, [currentRules, currentTargets, isAndroid, setAppBlocking]);

  useEffect(() => {
    if (rules === undefined) return;
    for (const optimisticRule of optimisticEnforcementRules) {
      if (rules.some((rule) => rule._id === optimisticRule._id)) {
        clearOptimisticEnforcementRule(optimisticRule._id);
      }
    }
  }, [rules, optimisticEnforcementRules]);

  useEffect(() => {
    if (rules === undefined) return;
    const projection = getEnforcementProjection();
    if (projection !== null && isEnforcementProjectionSynced(rules, projection)) {
      clearEnforcementProjection();
    }
  }, [rules]);

  const enforcingRules = useMemo(() => {
    if (rules === undefined) return [];
    return getEnforcingRules(currentRules, scheduleNow);
  }, [rules, currentRules, scheduleNow]);

  const breakPauseRules = useMemo(() => {
    if (rules === undefined) return [];
    return getRulesOnBreakPause(currentRules, scheduleNow);
  }, [rules, currentRules, scheduleNow]);

  const canClaimEnforcement = canClaimNativeAppEnforcement(appBlocking, { isNativePlatform });
  const showEnforcingBanner = enforcingRules.length > 0 && canClaimEnforcement && !reorderMode;
  const showBreakPauseBanner =
    !showEnforcingBanner && breakPauseRules.length > 0 && canClaimEnforcement && !reorderMode;
  const permissionGateReason = androidAppBlockingGateReason(appBlocking, { isAndroid });
  const showPermissionGate = !reorderMode && permissionGateReason !== null;

  const openPermissionGateSettings = useCallback(() => {
    if (permissionGateReason === "usage_access_not_granted") {
      void openUsagePermissionSettings().then(() => refreshAppBlocking());
      return;
    }
    void openAndroidOverlaySettings().then(() => refreshAppBlocking());
  }, [permissionGateReason, refreshAppBlocking]);

  const saveSmartBlockWindow = async (rule: Rule, window: RuleConfig["window"]) => {
    if (requiresSwipeUnlockToMutateRule(rule, scheduleNow, { rules: currentRules, targets: currentTargets })) {
      nudgeSwipeUnlock();
      return;
    }
    setPendingId(rule._id);
    try {
      await updateSmartBlockWindow({ anonymousDeviceId, id: rule._id, window });
      setSmartBlockRuleEditor(null);
      showMessage(t.rules.ruleUpdated);
    } catch (error) {
      showMessage(error instanceof Error ? error.message : t.rules.ruleSaveFailed, "error");
    } finally {
      setPendingId(null);
    }
  };

  const saveRule = async () => {
    if (!ruleDraft) return;
    const id = ruleDraft._id;
    const rule = ruleConfigFromDraft(ruleDraft);
    setSaving("rule");
    try {
      await upsertRule({ anonymousDeviceId, id, rule });
      setRuleDraft(null);
      showMessage(id ? t.rules.ruleUpdated : t.rules.ruleCreated);
    } catch (error) {
      showMessage(error instanceof Error ? error.message : t.rules.ruleSaveFailed, "error");
    } finally {
      setSaving(null);
    }
  };

  const saveTarget = async () => {
    if (!targetDraft) return;
    const id = targetDraft._id;
    const target = targetConfigFromDraft(targetDraft);
    const wasNew = !id;
    setSaving("target");
    try {
      const { id: savedId } = await upsertTarget({ anonymousDeviceId, id, target });
      setTargetDraft(null);
      setTargetEditorOrigin("standalone");
      if (wasNew && ruleDraft) {
        setRuleDraft((draft) =>
          draft && draft.targetIds.length < 3 ? { ...draft, targetIds: [...draft.targetIds, savedId] } : draft,
        );
      }
      if (wasNew && quickStartTargetEditor) {
        setQuickStartTargetIds((ids) => (ids.length < 3 ? [...ids, savedId] : ids));
        setQuickStartTargetEditor(false);
      }
      showMessage(wasNew ? t.rules.targetCreated : t.rules.targetUpdated);
    } catch (error) {
      showMessage(error instanceof Error ? error.message : t.rules.targetSaveFailed, "error");
    } finally {
      setSaving(null);
    }
  };

  const toggleRule = async (rule: Rule) => {
    const nowMs = scheduleNow.getTime();
    const timeZone = getDeviceTimeZone();
    const clock = { ...getDeviceLocalScheduleParts(nowMs, timeZone), nowMs };
    const forcedSuspended = isUserForcedStopSuppression(rule, clock);

    if (requiresSwipeUnlockToMutateRule(rule, scheduleNow, { rules: currentRules, targets: currentTargets })) {
      nudgeSwipeUnlock();
      return;
    }

    setPendingId(rule._id);
    try {
      if (forcedSuspended) {
        const projectedRules = projectRulesAfterResume(currentRules, rule._id, nowMs);
        setEnforcementProjection(projectedRules);
        await syncEnforcementToNative(projectedRules, currentTargets, new Date(nowMs));
        await resumeForcedStopMutation({ anonymousDeviceId, id: rule._id, nowMs, timeZone });
      } else {
        await setRuleEnabled({ anonymousDeviceId, id: rule._id, enabled: !rule.enabled });
      }
    } catch (error) {
      if (forcedSuspended) {
        clearEnforcementProjection();
      }
      showMessage(error instanceof Error ? error.message : t.rules.ruleSaveFailed, "error");
    } finally {
      setPendingId(null);
    }
  };

  const removeRule = async (rule: Rule) => {
    setDeleteConfirm(null);
    setPendingId(rule._id);
    try {
      await removeRuleMutation({ anonymousDeviceId, id: rule._id });
      showMessage(t.rules.ruleDeleted);
    } finally {
      setPendingId(null);
    }
  };

  const removeTarget = async (target: Target) => {
    setDeleteConfirm(null);
    setPendingId(target._id);
    setQuickStartTargetIds((ids) => ids.filter((id) => id !== target._id));
    try {
      await removeTargetMutation({ anonymousDeviceId, id: target._id });
      showMessage(t.rules.targetDeleted);
    } finally {
      setPendingId(null);
    }
  };

  const enabledCount = visibleRules.filter((rule) => rule.enabled).length;
  const showQuickStartFab =
    !reorderMode &&
    (!canClaimEnforcement || (enforcingRules.length === 0 && breakPauseRules.length === 0));
  const bothSectionsEmpty =
    rules !== undefined &&
    visibleRules.length === 0 &&
    targets !== undefined &&
    currentTargets.length === 0;

  return (
    <>
      <style>{WOBBLE_CSS}{HIGHLIGHT_CSS}</style>
      <TabPageShell
        className={`${TAB_BODY_COMPACT_TIMEFRAME_CLASS} ${
          showQuickStartFab && bothSectionsEmpty
            ? "pb-[calc(7rem+3.75rem+max(12px,env(safe-area-inset-bottom)))]"
            : "pb-28"
        }`}
        fixed={
          <TabPageHeader
            title={t.rules.title}
            actions={
              <button
                type="button"
                onClick={toggleReorder}
                className={`icon-button shrink-0${reorderMode ? " text-[oklch(72%_0.09_155)]" : ""}`}
                aria-label={reorderMode ? t.rules.doneReordering : t.rules.reorder}
              >
                <GripVertical size={20} />
              </button>
            }
          />
        }
      >
      <TransientStatusBanner
        message={message}
        variant={messageVariant}
        onDismiss={dismissMessage}
        dismissLabel={t.rules.dismiss}
      />

      <AnimatePresence mode="wait">
        {showPermissionGate ? (
          <motion.div
            key="app-blocking-permission-gate"
            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
            transition={prefersReducedMotion ? { duration: 0.15 } : BANNER_SPRING}
            className="mb-2"
          >
            <button
              type="button"
              onClick={openPermissionGateSettings}
              className="liquid-glass-dashed-cta flex min-h-11 w-full items-center gap-3 px-3 py-4 text-left transition-transform active:scale-[0.99]"
              aria-label={t.rules.appBlockingPermissionGate.bannerAria}
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[oklch(72%_0.09_155)] text-[oklch(12%_0.015_160)]">
                {permissionGateReason === "usage_access_not_granted" ? (
                  <ShieldAlert size={18} aria-hidden />
                ) : (
                  <Layers size={18} aria-hidden />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm leading-5 text-[oklch(82%_0.025_155)]">
                  {permissionGateReason === "usage_access_not_granted"
                    ? t.rules.appBlockingPermissionGate.usageBanner
                    : t.rules.appBlockingPermissionGate.overlayBanner}
                </span>
                <span className="mt-1 block text-xs font-semibold uppercase tracking-wide text-[oklch(72%_0.09_155)]">
                  {permissionGateReason === "usage_access_not_granted"
                    ? t.rules.appBlockingPermissionGate.usageCta
                    : t.rules.appBlockingPermissionGate.overlayCta}
                </span>
              </span>
            </button>
          </motion.div>
        ) : null}
        {showEnforcingBanner ? (
          <motion.div
            key="active-enforcement-banner"
            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
            transition={prefersReducedMotion ? { duration: 0.15 } : BANNER_SPRING}
          >
            <ActiveEnforcementBanner
              rules={enforcingRules}
              targetsById={targetsById}
              now={scheduleNow}
              prefersReducedMotion={Boolean(prefersReducedMotion)}
              variant="enforcement"
            />
          </motion.div>
        ) : showBreakPauseBanner ? (
          <motion.div
            key="break-pause-banner"
            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
            transition={prefersReducedMotion ? { duration: 0.15 } : BANNER_SPRING}
          >
            <ActiveEnforcementBanner
              rules={breakPauseRules}
              targetsById={targetsById}
              now={scheduleNow}
              prefersReducedMotion={Boolean(prefersReducedMotion)}
              variant="pause"
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

      <section className="grid gap-2">
        <SectionHeader
          title={t.rules.activeRulesTitle}
          subtitle={rules === undefined ? t.rules.activeRulesLoading : enabledCount > 0 ? formatTemplate(t.rules.activeRulesEnforcing, { count: enabledCount }) : t.rules.activeRulesEmptySchedule}
          addAriaLabel={formatTemplate(t.rules.addSectionAria, { title: t.rules.activeRulesTitle })}
          onAdd={() => setRuleDraft(emptyRuleDraft())}
          prominent
        />
        {rules === undefined ? <CardSkeleton kind="rule" /> : null}
        {rules && visibleRules.length === 0 ? (
          <EmptyState
            icon={<CalendarClock size={22} aria-hidden />}
            title={t.rules.noActiveRulesTitle}
            body={t.rules.noActiveRulesBody}
            actionLabel={t.rules.newRule}
            onAction={() => setRuleDraft(emptyRuleDraft())}
          />
        ) : null}
        {visibleRules.length > 0 ? (
          reorderMode ? (
            <Reorder.Group
              as="div"
              axis="y"
              values={reorderRules}
              onReorder={setReorderRules}
              className="rules-row-list"
            >
              {reorderRules.map((rule, index) => (
                <Reorder.Item
                  as="div"
                  key={rule._id}
                  value={rule}
                  style={{ touchAction: "pan-y" }}
                >
                  <div
                    className={`tap-row px-2 py-2${prefersReducedMotion ? "" : " wobble-card"}`}
                    style={{ animationDelay: `${index * 60}ms`, cursor: "grab", touchAction: "pan-y" }}
                  >
                    <RuleRowContent rule={rule} targetsById={targetsById} scheduleNow={scheduleNow} />
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>
          ) : (
            <div className="rules-row-list">
              {displayedRules.map((rule) => (
                <RuleRow
                  key={rule._id}
                  rule={rule}
                  targetsById={targetsById}
                  scheduleNow={scheduleNow}
                  busy={pendingId === rule._id}
                  highlightVariant={highlightRuleId === rule._id ? highlightVariant : null}
                  deleteOpen={deleteConfirm?.kind === "rule" && deleteConfirm.id === rule._id}
                  deleteCovered={
                    deleteConfirm?.kind === "rule" && deleteConfirm.id === rule._id
                      ? Boolean(deleteConfirm.covered)
                      : false
                  }
                  onDeleteOpen={() => {
                    if (requiresSwipeUnlockToMutateRule(rule, scheduleNow, { rules: currentRules, targets: currentTargets })) {
                      nudgeSwipeUnlock();
                      return;
                    }
                    const covered = isRuleFullyCoveredByAnotherEnforcingRule(
                      rule,
                      currentRules,
                      currentTargets,
                      scheduleNow,
                    );
                    setDeleteConfirm({ kind: "rule", id: rule._id, covered: covered || undefined });
                  }}
                  onDeleteClose={() => setDeleteConfirm(null)}
                  onToggle={() => void toggleRule(rule)}
                  onEdit={() => {
                    if (rules !== undefined && ruleDisplaysSmartBlockBadge(rule, targetsById)) {
                      if (requiresSwipeUnlockToMutateRule(rule, scheduleNow, { rules: currentRules, targets: currentTargets })) {
                        nudgeSwipeUnlock();
                        return;
                      }
                      if (isRuleActivelyEnforcingForSwipeGuard(rule, scheduleNow)) {
                        setSmartBlockNotEditable({ kind: "rule" });
                        return;
                      }
                      setSmartBlockRuleEditor(rule);
                      return;
                    }
                    if (requiresSwipeUnlockToMutateRule(rule, scheduleNow, { rules: currentRules, targets: currentTargets })) {
                      nudgeSwipeUnlock();
                      return;
                    }
                    setRuleDraft({ _id: rule._id, enabled: rule.enabled, targetIds: rule.targetIds, days: rule.days, window: rule.window });
                  }}
                  onRemove={() => void removeRule(rule)}
                />
              ))}
            </div>
          )
        ) : null}
      </section>

      <section className="grid gap-2">
        <SectionHeader
          title={t.rules.targetsTitle}
          subtitle={targets === undefined ? t.rules.activeRulesLoading : currentTargets.length > 0 ? formatTemplate(t.rules.targetsSaved, { count: currentTargets.length }) : t.rules.targetsEmptySubtitle}
          addAriaLabel={formatTemplate(t.rules.addSectionAria, { title: t.rules.targetsTitle })}
          onAdd={() => {
            openTargetEditor(emptyTargetDraft(), "standalone");
          }}
        />
        {targets === undefined ? <CardSkeleton kind="target" /> : null}
        {targets && currentTargets.length === 0 ? (
          <EmptyState
            icon={<Globe size={22} aria-hidden />}
            title={t.rules.noTargetsTitle}
            body={t.rules.noTargetsBody}
            actionLabel={t.rules.newTarget}
            onAction={() => {
              openTargetEditor(emptyTargetDraft(), "standalone");
            }}
          />
        ) : null}
        {currentTargets.length > 0 ? (
          reorderMode ? (
            <Reorder.Group
              as="div"
              axis="y"
              values={reorderTargets}
              onReorder={setReorderTargets}
              className="rules-row-list"
            >
              {reorderTargets.map((target, index) => (
                <Reorder.Item
                  as="div"
                  key={target._id}
                  value={target}
                  style={{ touchAction: "pan-y" }}
                >
                  <div
                    className={`tap-row px-2 py-2${prefersReducedMotion ? "" : " wobble-card"}`}
                    style={{ animationDelay: `${index * 60}ms`, cursor: "grab", touchAction: "pan-y" }}
                  >
                    <TargetRowContent
                      target={target}
                      showSmartBlockBadge={targetDisplaysSmartBlockBadge(target._id, currentRules, targetsById)}
                    />
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>
          ) : (
            <div className="rules-row-list">
              {displayedTargets.map((target) => (
                <TargetRow
                  key={target._id}
                  target={target}
                  rules={currentRules}
                  targetsById={targetsById}
                  busy={pendingId === target._id}
                  deleteOpen={deleteConfirm?.kind === "target" && deleteConfirm.id === target._id}
                  deleteCovered={
                    deleteConfirm?.kind === "target" && deleteConfirm.id === target._id
                      ? Boolean(deleteConfirm.covered)
                      : false
                  }
                  onDeleteOpen={() => {
                    if (isTargetLockedByEnforcingRules(target._id, currentRules, currentTargets, scheduleNow)) {
                      nudgeSwipeUnlock();
                      return;
                    }
                    const covered = isTargetFullyCoveredByAnotherEnforcingRule(
                      target,
                      currentRules,
                      currentTargets,
                      scheduleNow,
                    );
                    setDeleteConfirm({ kind: "target", id: target._id, covered: covered || undefined });
                  }}
                  onDeleteClose={() => setDeleteConfirm(null)}
                  onEdit={() => {
                    if (rules !== undefined && targets !== undefined && targetDisplaysSmartBlockBadge(target._id, currentRules, targetsById)) {
                      setSmartBlockNotEditable({ kind: "target" });
                      return;
                    }
                    if (isTargetLockedByEnforcingRules(target._id, currentRules, currentTargets, scheduleNow)) {
                      nudgeSwipeUnlock();
                      return;
                    }
                    openTargetEditor(
                      {
                        _id: target._id,
                        name: target.name,
                        apps: target.apps,
                        domains: target.domains,
                        domainCategories: target.domainCategories,
                        selection: target.selection,
                      },
                      "standalone",
                    );
                  }}
                  onRemove={() => void removeTarget(target)}
                />
              ))}
            </div>
          )
        ) : null}
      </section>

      <RuleEditorSheet
        open={ruleDraft !== null}
        draft={ruleDraft ?? lastRuleDraft.current}
        targets={displayedTargets}
        rules={rules === undefined ? undefined : currentRules}
        pending={saving === "rule"}
        onChange={setRuleDraft}
        onCreateTarget={() => {
          openTargetEditor(emptyTargetDraft(), "rule");
        }}
        onClose={() => setRuleDraft(null)}
        onSave={() => void saveRule()}
      />

      <TargetEditorSheet
        open={targetDraft !== null}
        draft={targetDraft ?? lastTargetDraft.current}
        baselineDraft={targetBaseline}
        origin={targetEditorOrigin}
        pending={saving === "target"}
        onChange={setTargetDraft}
        onClose={() => {
          setTargetDraft(null);
          setQuickStartTargetEditor(false);
          setTargetEditorOrigin("standalone");
        }}
        onSave={() => void saveTarget()}
      />

      <SmartBlockRuleWindowDialog
        open={smartBlockRuleEditor !== null}
        rule={smartBlockRuleEditor}
        pending={smartBlockRuleEditor !== null && pendingId === smartBlockRuleEditor._id}
        onClose={() => setSmartBlockRuleEditor(null)}
        onSave={(window) => {
          if (smartBlockRuleEditor) void saveSmartBlockWindow(smartBlockRuleEditor, window);
        }}
      />

      <SmartBlockNotEditableDialog
        open={smartBlockNotEditable !== null}
        kind={smartBlockNotEditable?.kind ?? "target"}
        onClose={() => setSmartBlockNotEditable(null)}
      />

      <VpnInstallPromptModal
        open={vpnInstallPromptOpen}
        rules={currentRules}
        targets={currentTargets}
        scheduleNow={scheduleNow}
        onClose={() => {
          const domainsKey = deriveActiveBlockedDomains(currentRules, currentTargets, scheduleNow).join(",");
          vpnInstallPromptDismissedKey.current = domainsKey;
          setVpnInstallPromptOpen(false);
        }}
        onInstalled={() => {
          vpnInstallPromptDismissedKey.current = null;
          setVpnInstallPromptOpen(false);
        }}
      />

      {showQuickStartFab ? (
        <QuickStartFab fabIcon={quickStartPrefs?.fabIcon} onOpen={() => setQuickStartOpen(true)} />
      ) : null}

      <QuickStartDrawer
        open={quickStartOpen}
        targets={targets}
        rules={rules}
        fabIcon={quickStartPrefs?.fabIcon}
        recentSessions={quickStartPrefs?.recentSessions ?? []}
        savedSessions={quickStartPrefs?.savedSessions ?? []}
        sessionActive={activeQuickSession !== undefined}
        selectedTargetIds={quickStartTargetIds}
        durationMinutes={quickStartDuration}
        onSelectedTargetIdsChange={setQuickStartTargetIds}
        onDurationChange={setQuickStartDuration}
        onFabIconChange={(icon) => void setQuickStartFabIcon({ anonymousDeviceId, fabIcon: icon })}
        onSaveFromRecent={(targetIds, durationMinutes, name) =>
          void saveQuickSession({ anonymousDeviceId, targetIds, durationMinutes, name })
        }
        onApplySession={(targetIds, durationMinutes) => void applyQuickSession(targetIds, durationMinutes)}
        onCreateTarget={() => {
          setQuickStartTargetEditor(true);
          openTargetEditor(emptyTargetDraft(), "quickStart");
        }}
        onClose={() => setQuickStartOpen(false)}
      />
      </TabPageShell>
    </>
  );
}

function SectionHeader({
  title,
  subtitle,
  onAdd,
  addAriaLabel,
  prominent = false,
}: {
  title: string;
  subtitle: string;
  onAdd: () => void;
  addAriaLabel: string;
  prominent?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="truncate text-xs text-[oklch(64%_0.025_155)]">{subtitle}</p>
      </div>
      <button
        type="button"
        onClick={onAdd}
        aria-label={addAriaLabel}
        className={
          prominent
            ? "grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[oklch(72%_0.09_155)] text-[oklch(12%_0.015_160)] shadow-[0_8px_22px_oklch(72%_0.09_155/0.28)] transition-transform active:scale-95"
            : "liquid-glass-icon-ring grid h-10 w-10 shrink-0 place-items-center text-[oklch(84%_0.04_155)] transition-transform active:scale-95"
        }
      >
        <Plus size={prominent ? 22 : 18} aria-hidden />
      </button>
    </div>
  );
}

function DeleteConfirmPopover({
  open,
  kind,
  covered = false,
  busy,
  onOpen,
  onClose,
  onConfirm,
}: {
  open: boolean;
  kind: "rule" | "target";
  covered?: boolean;
  busy: boolean;
  onOpen: () => void;
  onClose: (focusTrigger?: boolean) => void;
  onConfirm: () => void;
}) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const prefersReducedMotion = useReducedMotion();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const dialogId = useId();

  const close = useCallback(
    (focusTrigger = false) => {
      onClose(focusTrigger);
      if (focusTrigger) triggerRef.current?.focus();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close(true);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  const centeredTransform = { x: "-50%", y: "-50%" } as const;

  const confirmMessage = covered
    ? kind === "rule"
      ? t.rules.deleteRuleCoveredConfirm
      : t.rules.deleteTargetCoveredConfirm
    : kind === "rule"
      ? t.rules.deleteRuleConfirm
      : t.rules.deleteTargetConfirm;
  const confirmAria = covered
    ? kind === "rule"
      ? t.rules.deleteRuleCoveredConfirmAria
      : t.rules.deleteTargetCoveredConfirmAria
    : kind === "rule"
      ? t.rules.deleteRuleConfirmAria
      : t.rules.deleteTargetConfirmAria;

  return (
    <div className="relative shrink-0" onClick={(event) => event.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        className="ghost-icon-action"
        disabled={busy}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? dialogId : undefined}
        aria-label={formatTemplate(t.rules.deleteKindAria, { kind })}
        onClick={() => (open ? close(true) : onOpen())}
      >
        <Trash2 size={16} aria-hidden />
      </button>

      {typeof document !== "undefined"
        ? createPortal(
            <AnimatePresence>
              {open ? (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    aria-hidden="true"
                    onPointerDown={() => close(true)}
                  />
                  <motion.div
                    id={dialogId}
                    role="dialog"
                    aria-label={confirmAria}
                    className="liquid-glass-pill liquid-glass-pill-delete-confirm fixed left-1/2 top-1/2 z-50 w-[min(16rem,calc(100vw-2rem))] overflow-hidden rounded-xl p-2.5"
                    style={{ transformOrigin: "center center" }}
                    initial={
                      prefersReducedMotion
                        ? { opacity: 0, ...centeredTransform }
                        : { opacity: 0, scale: 0.96, ...centeredTransform }
                    }
                    animate={{ opacity: 1, scale: 1, ...centeredTransform }}
                    exit={
                      prefersReducedMotion
                        ? { opacity: 0, ...centeredTransform }
                        : { opacity: 0, scale: 0.96, ...centeredTransform }
                    }
                    transition={prefersReducedMotion ? { duration: 0 } : POPOVER_SPRING}
                    onPointerDown={(event) => event.stopPropagation()}
                  >
                    <p className="px-1 pb-2 text-sm font-medium text-[oklch(88%_0.02_150)]">
                      {confirmMessage}
                    </p>
                    <div className="flex items-center justify-end gap-2">
                      <button
                        ref={cancelRef}
                        type="button"
                        className="min-h-[44px] rounded-lg px-3 text-sm font-semibold text-[oklch(74%_0.025_155)] transition-colors active:bg-[oklch(24%_0.025_165)]"
                        onClick={() => close(true)}
                      >
                        {t.common.cancel}
                      </button>
                      <button
                        type="button"
                        className="min-h-[44px] rounded-lg bg-[oklch(52%_0.14_25)] px-3 text-sm font-semibold text-[oklch(96%_0.02_25)] transition-colors active:scale-[0.98] disabled:opacity-60"
                        disabled={busy}
                        onClick={() => {
                          close();
                          onConfirm();
                        }}
                      >
                        {t.common.delete}
                      </button>
                    </div>
                  </motion.div>
                </>
              ) : null}
            </AnimatePresence>,
            document.body,
          )
        : null}
    </div>
  );
}

function SmartBlockNotEditableDialog({
  open,
  kind,
  onClose,
}: {
  open: boolean;
  kind: "rule" | "target";
  onClose: () => void;
}) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const prefersReducedMotion = useReducedMotion();
  const dismissRef = useRef<HTMLButtonElement>(null);
  const dialogId = useId();

  const close = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  useEffect(() => {
    if (open) dismissRef.current?.focus();
  }, [open]);

  const centeredTransform = { x: "-50%", y: "-50%" } as const;

  return typeof document !== "undefined"
    ? createPortal(
        <AnimatePresence>
          {open ? (
            <>
              <div className="fixed inset-0 z-40" aria-hidden="true" onPointerDown={close} />
              <motion.div
                id={dialogId}
                role="dialog"
                aria-label={
                  kind === "rule"
                    ? t.rules.smartBlockRuleNotEditableAria
                    : t.rules.smartBlockTargetNotEditableAria
                }
                className="liquid-glass-pill liquid-glass-pill-delete-confirm fixed left-1/2 top-1/2 z-50 w-[min(16rem,calc(100vw-2rem))] overflow-hidden rounded-xl p-2.5"
                style={{ transformOrigin: "center center" }}
                initial={
                  prefersReducedMotion
                    ? { opacity: 0, ...centeredTransform }
                    : { opacity: 0, scale: 0.96, ...centeredTransform }
                }
                animate={{ opacity: 1, scale: 1, ...centeredTransform }}
                exit={
                  prefersReducedMotion
                    ? { opacity: 0, ...centeredTransform }
                    : { opacity: 0, scale: 0.96, ...centeredTransform }
                }
                transition={prefersReducedMotion ? { duration: 0 } : POPOVER_SPRING}
                onPointerDown={(event) => event.stopPropagation()}
              >
                <p className="px-1 pb-2 text-sm font-medium text-[oklch(88%_0.02_150)]">
                  {kind === "rule"
                    ? t.rules.smartBlockRuleNotEditable
                    : t.rules.smartBlockTargetNotEditable}
                </p>
                <div className="flex items-center justify-end">
                  <button
                    ref={dismissRef}
                    type="button"
                    className="min-h-[44px] rounded-lg px-3 text-sm font-semibold text-[oklch(74%_0.025_155)] transition-colors active:bg-[oklch(24%_0.025_165)]"
                    onClick={close}
                  >
                    {t.common.close}
                  </button>
                </div>
              </motion.div>
            </>
          ) : null}
        </AnimatePresence>,
        document.body,
      )
    : null;
}

function SmartBlockBadge() {
  const t = getTranslations(useScreen0Language().languageCode);
  return (
    <span className="liquid-glass-badge px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[oklch(78%_0.06_155)]">
      {t.rules.smartBlockBadge}
    </span>
  );
}

function ForcedSuspendBadge() {
  const t = getTranslations(useScreen0Language().languageCode);
  return (
    <span className="liquid-glass-badge px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[oklch(78%_0.10_75)]">
      {t.rules.forcedSuspend.badge}
    </span>
  );
}

function RuleRowContent({
  rule,
  targetsById,
  scheduleNow,
}: {
  rule: Rule;
  targetsById: Map<string, Target>;
  scheduleNow: Date;
}) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const timeZone = getDeviceTimeZone();
  const clock = {
    ...getDeviceLocalScheduleParts(scheduleNow.getTime(), timeZone),
    nowMs: scheduleNow.getTime(),
  };
  const forcedSuspended = isUserForcedStopSuppression(rule, clock);
  const ruleTargets = rule.targetIds.map((id) => targetsById.get(id)).filter((target): target is Target => Boolean(target));
  const ruleName = ruleTargets.map((target) => target.name).join(", ") || t.rules.noTargets;
  const iconApps = iconAppsForRule(rule, targetsById);
  const dedupedDomains = dedupeDomainsFromTargets(ruleTargets);
  const blockSummary = ruleBlockSummaryText(ruleTargets);
  const autoResumeLabel =
    forcedSuspended && rule.suppressedUntil !== undefined
      ? formatTemplate(t.rules.forcedSuspend.autoResume, {
          time: new Intl.DateTimeFormat(getScreen0IntlLocale(languageCode), {
            timeZone,
            weekday: "short",
            hour: "numeric",
            minute: "2-digit",
          }).format(new Date(rule.suppressedUntil)),
        })
      : null;

  return (
    <div className={`flex items-center gap-2 transition-opacity ${rule.enabled ? "" : "opacity-70"}`}>
      <div className="min-w-0 flex-1 px-1 py-1">
        <div className="grid gap-1.5">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h3 className="truncate text-sm font-semibold">{ruleName}</h3>
            {forcedSuspended ? <ForcedSuspendBadge /> : null}
            {!forcedSuspended && ruleDisplaysSmartBlockBadge(rule, targetsById) ? <SmartBlockBadge /> : null}
          </div>
          {forcedSuspended ? (
            <p className="text-xs text-[oklch(74%_0.06_75)]" role="status">
              {t.rules.forcedSuspend.subtitle}
              {autoResumeLabel ? (
                <>
                  {" "}
                  <span className="text-[oklch(66%_0.04_75)]">{autoResumeLabel}</span>
                </>
              ) : null}
            </p>
          ) : null}
          <p className="flex items-center gap-1 truncate text-xs text-[oklch(68%_0.025_155)]">
            <CalendarClock size={12} className="shrink-0" aria-hidden />
            {formatDays(rule.days)} · {formatWindow(rule.window, languageCode)}
          </p>
          <SummaryWithViewRow summary={blockSummary} targets={ruleTargets} />
          <CardAppIcons typedApps={iconApps} />
          <CardWebsites typedDomains={dedupedDomains} />
        </div>
      </div>
    </div>
  );
}

function RuleRow({
  rule,
  targetsById,
  scheduleNow,
  busy,
  highlightVariant,
  deleteOpen,
  deleteCovered,
  onDeleteOpen,
  onDeleteClose,
  onToggle,
  onEdit,
  onRemove,
}: {
  rule: Rule;
  targetsById: Map<string, Target>;
  scheduleNow: Date;
  busy: boolean;
  highlightVariant: RuleHighlightVariant | null;
  deleteOpen: boolean;
  deleteCovered: boolean;
  onDeleteOpen: () => void;
  onDeleteClose: () => void;
  onToggle: () => void;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const t = getTranslations(useScreen0Language().languageCode);
  const timeZone = getDeviceTimeZone();
  const clock = {
    ...getDeviceLocalScheduleParts(scheduleNow.getTime(), timeZone),
    nowMs: scheduleNow.getTime(),
  };
  const forcedSuspended = isUserForcedStopSuppression(rule, clock);
  const ruleTargets = rule.targetIds.map((id) => targetsById.get(id)).filter((target): target is Target => Boolean(target));
  const ruleName = ruleTargets.map((target) => target.name).join(", ") || t.rules.noTargets;
  const stackActionsVertically = dedupeDomainsFromTargets(ruleTargets).length > 0;

  const powerAria = forcedSuspended
    ? t.rules.forcedSuspend.resumeRuleAria
    : rule.enabled
      ? t.rules.disableRuleAria
      : t.rules.enableRuleAria;
  const powerClass = forcedSuspended
    ? "bg-[oklch(76%_0.11_75)] text-[oklch(18%_0.03_62)]"
    : rule.enabled
      ? "bg-[oklch(72%_0.09_155)] text-[oklch(12%_0.015_160)]"
      : "liquid-glass-icon-ring text-[oklch(64%_0.025_155)]";

  return (
    <article
      id={`rule-row-${rule._id}`}
      className={`tap-row flex items-center gap-2 px-2 py-2${
        highlightVariant === "insight"
          ? " rule-row-insight-highlight"
          : highlightVariant === "activation"
            ? " rule-row-highlight"
            : ""
      }${forcedSuspended ? " bg-[oklch(18%_0.03_75/0.22)]" : ""}`}
    >
      <button
        type="button"
        onClick={onEdit}
        aria-label={formatTemplate(t.rules.editRuleAria, { name: ruleName })}
        className={`min-w-0 flex-1 rounded-lg px-1 py-1 text-left ${CARD_BODY_ACTIVE_CLASS}`}
      >
        <RuleRowContent rule={rule} targetsById={targetsById} scheduleNow={scheduleNow} />
      </button>
      <div
        className={`flex shrink-0 ${stackActionsVertically ? "flex-col items-center" : "items-center"}`}
        onClick={(event) => event.stopPropagation()}
      >
        <DeleteConfirmPopover
          open={deleteOpen}
          kind="rule"
          covered={deleteCovered}
          busy={busy}
          onOpen={onDeleteOpen}
          onClose={onDeleteClose}
          onConfirm={onRemove}
        />
        <button
          type="button"
          onClick={onToggle}
          disabled={busy}
          aria-pressed={rule.enabled}
          aria-label={powerAria}
          className={`grid min-h-[44px] min-w-[44px] shrink-0 place-items-center rounded-full transition-colors ${powerClass}`}
        >
          <Power size={16} aria-hidden />
        </button>
      </div>
    </article>
  );
}

function TargetRowContent({
  target,
  showSmartBlockBadge,
}: {
  target: Target;
  showSmartBlockBadge: boolean;
}) {
  const iconApps = iconAppsForTarget(target);
  const summary = targetContentSummary(target, {
    dedupeTypedAndPickerApps: showSmartBlockBadge,
    androidNative: canPickAndroidApps(),
  });

  return (
    <div className="min-w-0 flex-1 px-1 py-1">
      <div className="grid gap-1.5">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h3 className="truncate text-sm font-semibold text-[oklch(90%_0.02_150)]">{target.name}</h3>
          {showSmartBlockBadge ? <SmartBlockBadge /> : null}
        </div>
        <SummaryWithViewRow summary={summary} targets={target} />
        <CardAppIcons typedApps={iconApps} />
        <CardWebsiteCategories categoryIds={target.domainCategories ?? []} />
        <CardWebsites typedDomains={target.domains} />
      </div>
    </div>
  );
}

function TargetRow({
  target,
  rules,
  targetsById,
  busy,
  deleteOpen,
  deleteCovered,
  onDeleteOpen,
  onDeleteClose,
  onEdit,
  onRemove,
}: {
  target: Target;
  rules: Rule[];
  targetsById: Map<string, Target>;
  busy: boolean;
  deleteOpen: boolean;
  deleteCovered: boolean;
  onDeleteOpen: () => void;
  onDeleteClose: () => void;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const t = getTranslations(useScreen0Language().languageCode);
  const showSmartBlockBadge = targetDisplaysSmartBlockBadge(target._id, rules, targetsById);
  return (
    <article className="tap-row flex items-center gap-2 px-2 py-2">
      <button
        type="button"
        onClick={onEdit}
        aria-label={formatTemplate(t.rules.editTargetAria, { targetName: target.name })}
        className={`min-w-0 flex-1 rounded-lg px-1 py-1 text-left ${CARD_BODY_ACTIVE_CLASS}`}
      >
        <TargetRowContent target={target} showSmartBlockBadge={showSmartBlockBadge} />
      </button>
      <div className="flex shrink-0 self-center" onClick={(event) => event.stopPropagation()}>
        <DeleteConfirmPopover
          open={deleteOpen}
          kind="target"
          covered={deleteCovered}
          busy={busy}
          onOpen={onDeleteOpen}
          onClose={onDeleteClose}
          onConfirm={onRemove}
        />
      </div>
    </article>
  );
}

function EmptyState({
  icon,
  title,
  body,
  actionLabel,
  onAction,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <div className="liquid-glass-panel grid justify-items-center gap-3 px-4 py-7 text-center">
      <span className="grid size-11 place-items-center rounded-full bg-[oklch(20%_0.025_160)] text-[oklch(72%_0.08_155)]">{icon}</span>
      <div className="grid gap-1">
        <p className="text-base font-semibold text-[oklch(90%_0.02_150)]">{title}</p>
        <p className="max-w-[24rem] text-sm leading-6 text-[oklch(66%_0.025_155)]">{body}</p>
      </div>
      <button type="button" className="primary-action mt-1 min-h-11 px-5" onClick={onAction}>
        <Plus size={16} aria-hidden />
        {actionLabel}
      </button>
    </div>
  );
}

function CardSkeleton({ kind }: { kind: "rule" | "target" }) {
  return (
    <div className="rules-row-list">
      {[0, 1].map((index) => (
        <div key={index} className="tap-row flex items-center gap-2 px-2 py-2.5">
          <div className={`grid min-w-0 flex-1 gap-1.5 ${kind === "rule" ? "py-0.5" : ""}`}>
            <span className="h-3.5 w-32 rounded bg-[oklch(27%_0.02_165)]" />
            <span className="h-3 w-40 rounded bg-[oklch(23%_0.018_165)]" />
            {kind === "rule" ? (
              <>
                <span className="h-10 w-32 rounded bg-[oklch(23%_0.018_165)]" />
                <span className="h-5 w-32 rounded bg-[oklch(23%_0.018_165)]" />
              </>
            ) : (
              <>
                <span className="h-10 w-24 rounded bg-[oklch(23%_0.018_165)]" />
                <span className="h-5 w-28 rounded bg-[oklch(23%_0.018_165)]" />
              </>
            )}
          </div>
          <span className="size-11 shrink-0 rounded-full bg-[oklch(23%_0.018_165)]" />
          {kind === "rule" ? <span className="size-11 shrink-0 rounded-full bg-[oklch(23%_0.018_165)]" /> : null}
        </div>
      ))}
    </div>
  );
}
